import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Award, ChevronLeft, ChevronRight } from 'lucide-react';

export const PortfolioSectionHTAG = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const carouselRef = useRef(null);
  const touchStartX = useRef(0);
  
  const projects = [
    {
      name: 'Optique Ralph Petit',
      domain: 'optiqueralphpetit.fr',
      description: 'Site vitrine WordPress pour opticien. Présentation des services, prise de rendez-vous en ligne, galerie des montures, localisation et horaires.',
      technologies: ['WordPress', 'Design UX/UI', 'Prise RDV', 'Responsive Design'],
      color: 'from-cyan-600/20 to-blue-600/20',
      borderColor: 'border-cyan-500/30'
    },
    {
      name: 'Résidence Champenoise',
      domain: 'residencechampenoise-lancelotrouyer.fr',
      description: 'Site institutionnel pour résidence de tourisme. Présentation des chambres, système de réservation, galerie photo immersive et informations touristiques.',
      technologies: ['WordPress', 'Design UX/UI', 'Booking System', 'Responsive'],
      color: 'from-purple-600/20 to-pink-600/20',
      borderColor: 'border-purple-500/30'
    },
    {
      name: 'Le Panse Formation',
      domain: 'lepanse-formation.com',
      description: 'Plateforme de formation en ligne. Système de gestion des cours, suivi pédagogique, certification et espace apprenant intuitif et sécurisé.',
      technologies: ['LMS Custom', 'E-learning', 'Paiements', 'Responsive'],
      color: 'from-green-600/20 to-emerald-600/20',
      borderColor: 'border-green-500/30'
    },
    {
      name: 'Château Porcien',
      domain: 'chateauporcien.fr',
      description: 'Site institutionnel pour château historique. Présentation de la galerie, réservation de visites, événementiel, hébergement et restaurant. Immersion dans l\'histoire.',
      technologies: ['WordPress', 'Réservations', 'Galerie Photo', 'CMS'],
      color: 'from-amber-600/20 to-orange-600/20',
      borderColor: 'border-amber-500/30'
    },
  ];

  const getPreviewUrl = (domain) => {
    // Try https, let WordPress mShots capture; falls back to http if https not available
    return `https://s.wordpress.com/mshots/v1/http://${domain}?w=1200`;
  };

  const handlePrev = useCallback(() => {
    setCurrentIndex(prev => (prev === 0 ? projects.length - 1 : prev - 1));
  }, [projects.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => (prev >= projects.length - 1 ? 0 : prev + 1));
  }, [projects.length]);

  // Handle touch swipe for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  return (
    <section className="apple-section px-6 lg:px-8 relative">
      <div className="container mx-auto">
        {/* Titre */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Award className="w-6 h-6" style={{ color: '#0071e3' }} />
            <span className="text-sm font-medium" style={{ color: '#0071e3' }}>Références</span>
          </div>
          <h2 className="apple-title text-5xl lg:text-6xl mb-6">
            Nos réalisations
          </h2>
          <p className="apple-subtitle max-w-3xl mx-auto">
            Découvrez nos projets Web et créations graphiques pour divers partenaires
          </p>
        </motion.div>

        {/* Portfolio Web avec Carousel Horizontal */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="flex items-center justify-between mb-12">
            <h3 className="text-3xl font-bold" style={{ color: '#f5f5f7' }}>
              Réalisations Web
            </h3>
            <div className="flex gap-3">
              <button
                onClick={handlePrev}
                className="p-3 rounded-xl apple-card hover:scale-110 transition-transform"
              >
                <ChevronLeft size={20} style={{ color: '#f5f5f7' }} />
              </button>
              <button
                onClick={handleNext}
                className="p-3 rounded-xl apple-card hover:scale-110 transition-transform"
              >
                <ChevronRight size={20} style={{ color: '#f5f5f7' }} />
              </button>
            </div>
          </div>
          
          {/* Horizontal Scroll Container */}
          <div 
            ref={carouselRef}
            className="relative overflow-x-hidden overflow-y-visible py-4 cursor-grab active:cursor-grabbing"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <motion.div
              className="flex gap-8 w-full"
              animate={{ x: -currentIndex * 50 + '%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {projects.map((project, idx) => (
                <motion.div
                  key={idx}
                  onHoverStart={() => setHoveredCard(idx)}
                  onHoverEnd={() => setHoveredCard(null)}
                  className="flex-shrink-0 w-1/2"
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={`rounded-3xl overflow-hidden border ${project.borderColor} apple-card relative group shadow-xl`}>
                    {/* Preview screenshot */}
                    <div className="relative h-56 overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800">
                      <img
                        src={getPreviewUrl(project.domain)}
                        alt={`Page d'accueil ${project.name}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#000000cc] via-transparent to-transparent"></div>
                      {/* Badge Live */}
                      <a
                        href={`https://${project.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2 bg-blue-600 hover:bg-blue-700 transition-colors z-20"
                        style={{ color: '#f5f5f7' }}
                      >
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                        Visiter
                      </a>
                      {/* Animated Gradient Shine Effect */}
                      {hoveredCard === idx && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.15) 50%, transparent 100%)',
                            width: '100%',
                            height: '100%',
                            animation: 'shine-infinite 1.2s linear 3'
                          }}
                        />
                      )}
                    </div>

                    {/* Content */}
                    <div className={`p-8 bg-gradient-to-br ${project.color}`}>
                      <h4 className="font-bold text-2xl mb-2" style={{ color: '#f5f5f7', letterSpacing: '-0.02em' }}>
                        {project.name}
                      </h4>
                      <a
                        href={`https://${project.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-sm font-medium mb-4 transition-all hover:gap-2"
                        style={{ color: '#0071e3' }}
                      >
                        <span>{project.domain}</span>
                        <ExternalLink size={14} />
                      </a>
                      <p className="text-sm leading-relaxed mb-6" style={{ color: '#a1a1a6' }}>
                        {project.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 rounded-full text-xs font-medium border border-white/10"
                            style={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              color: '#a1a1a6'
                            }}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Progress Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {projects.map((_, idx) => (
              <motion.button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className="rounded-full transition-all"
                animate={{
                  width: idx === currentIndex ? '32px' : '8px',
                  backgroundColor: idx === currentIndex ? '#0071e3' : 'rgba(255, 255, 255, 0.2)',
                }}
                style={{ height: '8px' }}
              />
            ))}
          </div>
        </motion.div>

        {/* Designs Graphiques */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h3 className="text-3xl font-bold mb-12" style={{ color: '#f5f5f7' }}>
            Créations Graphiques
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Affiches Promotionnelles',
                description: 'Designs percutants pour campagnes marketing, événements et promotions saisonnières.',
                examples: ['Affiches 4x3', 'Prints A1/A2', 'Bannières digitales'],
                gradient: 'from-orange-600/20 to-red-600/20',
                borderColor: 'border-orange-500/30',
                iconGradient: 'from-orange-400 to-red-400'
              },
              {
                title: 'Brochures & Catalogues',
                description: 'Brochures tri-plis, catalogues produits, livrets informatifs avec mise en page professionnelle.',
                examples: ['Tri-plis', 'Catalogues A4', 'Dépliants'],
                gradient: 'from-blue-600/20 to-cyan-600/20',
                borderColor: 'border-blue-500/30',
                iconGradient: 'from-blue-400 to-cyan-400'
              },
              {
                title: 'Identité de Marque',
                description: 'Logo, charte graphique, cartes de visite, en-têtes - une identité cohérente et mémorable.',
                examples: ['Logos', 'Chartes graphiques', 'Cartes de visite'],
                gradient: 'from-purple-600/20 to-pink-600/20',
                borderColor: 'border-purple-500/30',
                iconGradient: 'from-purple-400 to-pink-400'
              },
            ].map((category, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`apple-card p-8 rounded-3xl border ${category.borderColor}`}
              >
                {/* Icon CSS */}
                <div className="mb-6">
                  {idx === 0 && (
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-orange-400 to-red-400 flex items-center justify-center">
                      <div className="w-8 h-6 bg-orange-600 rounded flex items-center justify-center">
                        <div className="w-4 h-3 border-2 border-orange-300 rounded-sm"></div>
                      </div>
                    </div>
                  )}
                  {idx === 1 && (
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center">
                      <div className="w-7 h-9 bg-blue-600 rounded border-2 border-blue-300">
                        <div className="h-1 bg-blue-300 m-1"></div>
                        <div className="h-1 bg-blue-300 m-1"></div>
                        <div className="h-1 bg-blue-300 m-1"></div>
                      </div>
                    </div>
                  )}
                  {idx === 2 && (
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                      <div className="relative w-8 h-8">
                        <div className="absolute w-6 h-6 border-3 border-purple-600 rounded-full"></div>
                        <div className="absolute w-3 h-3 bg-purple-600 rounded-full top-1 right-0"></div>
                      </div>
                    </div>
                  )}
                </div>
                
                <h4 className="font-bold text-xl mb-3" style={{ color: '#f5f5f7', letterSpacing: '-0.02em' }}>
                  {category.title}
                </h4>
                <p className="text-sm leading-relaxed mb-4" style={{ color: '#a1a1a6' }}>
                  {category.description}
                </p>
                <ul className="space-y-2">
                  {category.examples.map((example, i) => (
                    <li key={i} className="text-sm flex items-center space-x-2" style={{ color: '#86868b' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}></span>
                      <span>{example}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Processus de création */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="apple-card border border-blue-500/20 rounded-3xl p-12 text-center"
        >
          <h3 className="text-3xl font-bold mb-4" style={{ color: '#f5f5f7', letterSpacing: '-0.02em' }}>
            Votre projet est notre prochaine référence
          </h3>
          <p className="apple-subtitle max-w-2xl mx-auto mb-8">
            Qu'il s'agisse d'un site web complet, d'une refonte graphique ou d'une campagne de communication, nous mettons tout notre savoir-faire pour créer des solutions qui font la différence.
          </p>
          <a
            href="/?scroll=contact"
            className="inline-flex items-center space-x-3 apple-button"
          >
            <span>Discuter de votre projet</span>
            <ExternalLink size={20} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioSectionHTAG;
